import { FC } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, GraduationCap, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { LessonStatusIndicator } from './LessonStatusIndicator';
import { PremiumToggle } from './PremiumToggle';
import { DeleteLessonDialog } from './DeleteLessonDialog';
import { LessonLevelSelect } from './LessonLevelSelect';

interface LessonCardProps {
  lesson: {
    id: string;
    title: string;
    content_text: string;
    published: boolean;
    level: string;
    age_group: string;
    created_at: string;
    is_premium: boolean;
  };
  onDelete?: () => void;
}

export const LessonCard: FC<LessonCardProps> = ({ lesson, onDelete }) => {
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate(`/admin/lessons/edit/${lesson.id}`);
  };

  const getPlainTextDescription = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > 150 ? text.substring(0, 150) + '...' : text;
  };

  const getAgeGroupLabel = (ageGroup: string) => {
    const groups = {
      'kids': 'Niños',
      'teens': 'Adolescentes',
      'adults': 'Adultos'
    };
    return groups[ageGroup as keyof typeof groups] || ageGroup;
  };

  return (
    <Card className="p-4 hover:shadow-lg transition-shadow h-full flex flex-col justify-between">
      <div className="space-y-3">
        <div>
          <h3 className="font-semibold text-base md:text-lg line-clamp-2">{lesson.title}</h3>
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-primary" />
              <LessonLevelSelect
                lessonId={lesson.id}
                currentLevel={lesson.level}
                onUpdate={onDelete}
              />
            </div>
            <span className="inline-flex items-center gap-1 bg-secondary/10 text-secondary text-xs md:text-sm px-2 py-1 rounded">
              <Users className="h-4 w-4" />
              {getAgeGroupLabel(lesson.age_group)}
            </span>
          </div>
        </div>

        <p className="text-sm text-gray-600 line-clamp-3">
          {getPlainTextDescription(lesson.content_text)}
        </p>

        <div className="flex items-center justify-between gap-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <LessonStatusIndicator published={lesson.published} />
          </div>
          <PremiumToggle 
            lessonId={lesson.id}
            isPremium={lesson.is_premium}
            onUpdate={onDelete || (() => {})}
          />
        </div>

        <div className="text-xs text-gray-500">
          Creado el {format(new Date(lesson.created_at), "d 'de' MMMM, yyyy", { locale: es })}
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleEdit}
          className="flex-1 flex items-center justify-center gap-2 bg-primary/10 text-primary hover:bg-primary hover:text-white transition-colors"
        >
          <Pencil className="h-4 w-4" />
          Editar
        </Button>
        <DeleteLessonDialog
          lessonId={lesson.id}
          onDelete={onDelete}
        />
      </div>
    </Card>
  );
};